/**
 * Справка по разделам.
 *
 * Живёт отдельно от экранов и от кнопки: экранов десяток, и справка,
 * размазанная по ним, разойдётся с интерфейсом на второй правке. Здесь она
 * читается целиком — видно, где текст устарел.
 *
 * Тема выбирается по адресу, а не по внутреннему состоянию раздела: адрес
 * в этой системе и есть состояние экрана.
 */

export interface HelpTopic {
  /** Заголовок панели. */
  title: string;
  /** Два-три предложения о том, что это за экран и зачем он. */
  summary: string;
  /** Что человек может здесь сделать — по одному действию в строке. */
  actions: string[];
  /** Куда идти за подробностями. */
  guide: { label: string; file: string };
}

const USER_GUIDE = {
  label: "Как работать в PlanTask",
  file: "docs/17-РУКОВОДСТВО-ПОЛЬЗОВАТЕЛЯ.md",
};
const ADMIN_GUIDE = {
  label: "Руководство настройщика",
  file: "docs/16-РУКОВОДСТВО-НАСТРОЙЩИКА.md",
};

const TOPICS: Record<string, HelpTopic> = {
  tasks: {
    title: "Список задач",
    summary:
      "Основной рабочий экран. Отбор набирается переключателями в полосе над таблицей, " +
      "сложное условие — за кнопкой «Правило отбора». Пагинации нет: список догружается " +
      "сам при прокрутке.",
    actions: [
      "Щёлкните по статусу в полосе, чтобы оставить только его; повторный щелчок снимает отбор",
      "Отберите строки галочками — появится панель массовых действий",
      "Сохраните набранный отбор как представление: он станет вкладкой наверху",
      "Отправьте коллеге адрес страницы — отбор в нём, у него откроется тот же экран",
    ],
    guide: USER_GUIDE,
  },
  task: {
    title: "Карточка задачи",
    summary:
      "Всё о задаче на одном экране. Лента — одновременно переписка, журнал и способ ввода: " +
      "текст, смена статуса, срок и файл уходят одним действием.",
    actions: [
      "Выберите видимость записи: «Всем» видит и контрагент, «Сотрудникам» — только свои",
      "Поля со знаком = считаются формулой и не редактируются",
      "«Начать отсчёт» включает таймер; он живёт на сервере и переживает закрытую вкладку",
      "Расходы и списания времени попадают в отчёты и в карточку проекта",
    ],
    guide: USER_GUIDE,
  },
  project: {
    title: "Обзор проекта",
    summary:
      "Кто ведёт проект и от чьего имени, на кого работаем и с кем разговариваем, " +
      "сколько задач и сколько уже потрачено, что происходило.",
    actions: [
      "Щёлкните по цифре или по статусу — откроется список задач с тем же отбором",
      "Расходы приходят из задач проекта: записывают их в задаче, здесь виден итог",
      "Записи по проекту — то, что относится к проекту целиком, а не к одной задаче",
      "Роль «Руководитель» даёт права на все задачи проекта и его подпроектов",
    ],
    guide: USER_GUIDE,
  },
  planner: {
    title: "Планировщик",
    summary:
      "Доска из нескольких списков: что взять в работу, что сегодня, что ждёт вас. " +
      "Состав списков настраивается.",
    actions: [
      "Перетащите карточку между колонками мышью или с клавиатуры",
      "Изменение применяется сразу; если сервер откажет, карточка вернётся на место",
    ],
    guide: USER_GUIDE,
  },
  calendar: {
    title: "Календарь",
    summary: "Задачи по срокам — день, неделя, месяц, при необходимости с почасовой сеткой.",
    actions: [
      "Переключите масштаб и включите почасовую сетку — выбор сохранится в адресе",
      "«Выгрузить iCal» даёт ссылку для подписки: сроки появятся в вашем календаре",
    ],
    guide: USER_GUIDE,
  },
  gantt: {
    title: "Диаграмма Гантта",
    summary: "Сроки и зависимости между задачами.",
    actions: [
      "Сдвиньте задачу — зависимые сдвинутся вместе с ней",
      "«Записать базовый план» запоминает текущие сроки, чтобы потом видеть отклонение",
    ],
    guide: USER_GUIDE,
  },
  reports: {
    title: "Отчёты",
    summary:
      "Конструктор поверх задач, аналитик и списаний времени: отбор, группировка, " +
      "показатели и вид — таблица, график или сводная.",
    actions: [
      "Соберите отчёт и сохраните — он появится в списке слева",
      "Отправьте ссылку: она открывает отчёт вместе с параметрами",
    ],
    guide: USER_GUIDE,
  },
  contacts: {
    title: "Контакты",
    summary:
      "Люди и компании в одном списке: искать удобнее вместе. Поиск идёт по имени, " +
      "телефону, почте и ИНН.",
    actions: [
      "«Найти дубли» показывает похожие записи; склейка сохраняет переписку",
      "Карточка контакта настраивается так же, как карточка задачи",
    ],
    guide: USER_GUIDE,
  },
  settings: {
    title: "Настройка",
    summary:
      "Здесь система превращается в вашу: типы задач, поля, статусы, справочники, " +
      "аналитики, сценарии, права и оргструктура.",
    actions: [
      "Начните с типа задач и его статусов, поля добавляйте потом",
      "Новый сценарий заводите выключенным и проверяйте отладчиком на одной задаче",
      "«Диагностика» показывает очередь событий, размеры таблиц и медленные запросы",
      "«Конфигурация» выгружает настройку в YAML и переносит её в другой контур",
    ],
    guide: ADMIN_GUIDE,
  },
};

const DEFAULT_TOPIC: HelpTopic = {
  title: "PlanTask",
  summary:
    "Слева разделы и дерево проектов, наверху поиск и уведомления. " +
    "Ctrl + K открывает поиск по задачам, записям и файлам из любого места.",
  actions: [
    "Щёлкните по проекту — список отберётся по нему и по вложенным проектам",
    "Тема, плотность и срок сессии настраиваются в меню наверху",
  ],
  guide: USER_GUIDE,
};

/**
 * Тема справки по адресу страницы.
 *
 * Порядок проверок важен: `/tasks/502667` — это карточка, а не список,
 * и общее правило «начинается с /tasks» дало бы справку не о том экране.
 */
export function topicFor(pathname: string): HelpTopic {
  if (/^\/tasks\/\d+/.test(pathname)) {
    return TOPICS["task"] ?? DEFAULT_TOPIC;
  }
  if (/^\/projects\/\d+/.test(pathname)) {
    return TOPICS["project"] ?? DEFAULT_TOPIC;
  }
  const section = pathname.split("/").filter(Boolean)[0];
  return (section === undefined ? undefined : TOPICS[section]) ?? DEFAULT_TOPIC;
}
